import { useState } from "react";
import dayjs from "dayjs";

import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";
import { FaEye, FaInfoCircle } from "react-icons/fa";

import { Period } from "@/@types/generics/Period";
import { Procedure } from "@/@types/esus/Procedure";
import { SHORT_MONTHS } from "@/utils/enums/DateEnum";
import { ProfessionalCategoryEnum } from "@/utils/enums/ProfessionalCategoryEnum";
import { ResumeGrouped } from "@/services/esus/attendanceService";

import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";
import { AttendanceProcedureHistoryChart } from "./AttendanceProcedureHistoryChart";
import { BarChart } from "@/components/Charts/BarChart";
import { AttendanceProcedureRateByProcedureModal } from "./AttendanceProcedureRateByProcedureModal";

type Props = {
	proceduresAggregatedByPeriodAndProfessionalCategory: ResumeGrouped<
		Period,
		ResumeGrouped<string, object>
	>[];
	proceduresAggregatedByProcedureAndProfessionalCategory: ResumeGrouped<
		Procedure,
		ResumeGrouped<string, object>
	>[];
};

type ProfessionalAttendanceResumeType = "medic" | "nurse" | "nursingTechnician" | "others";

const professionalCategories = [
	ProfessionalCategoryEnum.MEDIC.valueOf(),
	ProfessionalCategoryEnum.MEDIC_GENERIC.valueOf(),
	ProfessionalCategoryEnum.MEDIC_PHYSICIAN.valueOf(),
	ProfessionalCategoryEnum.MEDIC_SURGEON.valueOf(),
	ProfessionalCategoryEnum.NURSE.valueOf(),
	ProfessionalCategoryEnum.NURSING_TECHNICIAN.valueOf(),
];

const professionalCategoriesByType: Record<ProfessionalAttendanceResumeType, string[] | undefined> =
	{
		medic: [
			ProfessionalCategoryEnum.MEDIC.valueOf(),
			ProfessionalCategoryEnum.MEDIC_GENERIC.valueOf(),
			ProfessionalCategoryEnum.MEDIC_PHYSICIAN.valueOf(),
			ProfessionalCategoryEnum.MEDIC_SURGEON.valueOf(),
		],
		nurse: [ProfessionalCategoryEnum.NURSE.valueOf()],
		nursingTechnician: [ProfessionalCategoryEnum.NURSING_TECHNICIAN.valueOf()],
		others: undefined,
	};

export function AttendanceProcedureDetails({
	proceduresAggregatedByPeriodAndProfessionalCategory,
	proceduresAggregatedByProcedureAndProfessionalCategory,
}: Readonly<Props>) {
	const [
		showAttendanceProcedureRateByProcedureModal,
		setShowAttendanceProcedureRateByProcedureModal,
	] = useState(false);
	const proceduresAggregatedByPeriodAndProfessionalCategorySorted =
		proceduresAggregatedByPeriodAndProfessionalCategory.sort((aggregatedA, aggregatedB) =>
			dayjs(new Date(aggregatedB.key.year, aggregatedB.key.month, 1)).isAfter(
				dayjs(new Date(aggregatedA.key.year, aggregatedA.key.month, 1))
			)
				? -1
				: 0
		);
	const periodProceduresSeries = proceduresAggregatedByPeriodAndProfessionalCategorySorted.map(
		(aggregatedAttendances) => aggregatedAttendances.total
	);
	const periodProceduresCategories =
		proceduresAggregatedByPeriodAndProfessionalCategorySorted?.map((period) =>
			period.key.year && period.key.month
				? `${period.key.year}/${SHORT_MONTHS[period.key.month - 1]}`
				: ""
		);

	const proceduresAggregatedByProcedureAndProfessionalCategorySorted =
		proceduresAggregatedByProcedureAndProfessionalCategory
			.sort((aggregatedA, aggregatedB) => aggregatedB.total - aggregatedA.total)
			.slice(0, 10);
	const proceduresAggregatedByProcedureAndProfessionalCategoryCategories =
		proceduresAggregatedByProcedureAndProfessionalCategorySorted
			.map((aggregatedAttendances) => aggregatedAttendances.key)
			.map((dashboard) =>
				dashboard.procedureCode ? `${dashboard.procedureCode} - ${dashboard.name}` : ""
			);
	const proceduresAggregatedByProcedureAndProfessionalCategorySeries = [
		{
			data: proceduresAggregatedByProcedureAndProfessionalCategorySorted.map(
				(professionalCategories) =>
					(professionalCategories.values || [])
						.filter((professionalCategory) =>
							professionalCategoriesByType.medic?.includes(professionalCategory.key)
						)
						?.map((professionalCategory) => professionalCategory.total)
						.reduce((acc, curr) => acc + curr, 0) || 0
			),
			name: "Médico",
		},
		{
			data: proceduresAggregatedByProcedureAndProfessionalCategorySorted.map(
				(professionalCategories) =>
					(professionalCategories.values || [])
						.filter((professionalCategory) =>
							professionalCategoriesByType.nurse?.includes(professionalCategory.key)
						)
						?.map((professionalCategory) => professionalCategory.total)
						.reduce((acc, curr) => acc + curr, 0) || 0
			),
			name: "Enfermeiro",
		},
		{
			data: proceduresAggregatedByProcedureAndProfessionalCategorySorted.map(
				(professionalCategories) =>
					(professionalCategories.values || [])
						.filter((professionalCategory) =>
							professionalCategoriesByType.nursingTechnician?.includes(
								professionalCategory.key
							)
						)
						?.map((professionalCategory) => professionalCategory.total)
						.reduce((acc, curr) => acc + curr, 0) || 0
			),
			name: "Tec. Enfermagem",
		},
		{
			data: proceduresAggregatedByProcedureAndProfessionalCategorySorted.map(
				(professionalCategoriesResume) =>
					(professionalCategoriesResume.values || [])
						.filter(
							(professionalCategoryResume) =>
								!professionalCategories.includes(professionalCategoryResume.key)
						)
						?.map((professionalCategoryResume) => professionalCategoryResume.total)
						.reduce((acc, curr) => acc + curr, 0) || 0
			),
			name: "Outros",
		},
	];

	return (
		<section>
			<Row className="p-2">
				<Col>
					<Card>
						<Card.Header>
							<Card.Title className="d-flex gap-2">
								Evolução histórica de procedimentos
								<CardOverlayTrigger
									header={"Procedimentos"}
									message={"Total de procedimentos realizados mensalmente"}
									icon={<FaInfoCircle />}
								/>
							</Card.Title>
						</Card.Header>
						<Card.Body style={{ height: 500 }}>
							<AttendanceProcedureHistoryChart
								series={[
									{
										data: periodProceduresSeries,
										name: "Procedimentos realizados",
									},
								]}
								categories={periodProceduresCategories}
							/>
						</Card.Body>
					</Card>
				</Col>
			</Row>
			<Row className="p-2">
				<Col>
					<Card>
						<Card.Header className="d-flex justify-content-between align-items-center">
							<Card.Title className="d-flex gap-2">
								Procedimentos mais utilizados
								<CardOverlayTrigger
									header={"Procedimentos mais utilizados"}
									message={
										"Total de procedimentos realizados no período por categoria profissional"
									}
									icon={<FaInfoCircle />}
								/>
							</Card.Title>
							<Button
								className="no-print h-25"
								variant="secondary"
								onClick={() => setShowAttendanceProcedureRateByProcedureModal(true)}
							>
								<FaEye className="me-2" />
								Detalhes
							</Button>
						</Card.Header>
						<Card.Body className="ps-0">
							<div className="overflow-y-scroll" style={{ height: 600 }}>
								<BarChart
									series={
										proceduresAggregatedByProcedureAndProfessionalCategorySeries
									}
									categories={
										proceduresAggregatedByProcedureAndProfessionalCategoryCategories
									}
									categoryTextWidth={380}
									minHeight={600}
									isStacked
								/>
							</div>
						</Card.Body>
					</Card>
				</Col>
			</Row>
			<AttendanceProcedureRateByProcedureModal
				aggregatedAttendanceProcedures={
					proceduresAggregatedByProcedureAndProfessionalCategory
				}
				show={showAttendanceProcedureRateByProcedureModal}
				onHide={() => setShowAttendanceProcedureRateByProcedureModal(false)}
			/>
		</section>
	);
}
